import { useCallback } from "react";
import { useScratchPayPurchase } from "../../../../services/scratchpay/scratchpay.service";
import { IPollingContext, useScratchPayPolling } from "./useScratchPayPolling";
import { IScratchPayPurchaseData } from "../../../../services/scratchpay/scratchpay.interface";
import { userActivityLogger } from "../../../../utils/mixpanel";
import { MIXPANEL_EVENTS } from "../../../../config/mixPanelEvents";

interface IInitiatePurchaseParams {
  purchaseData: IScratchPayPurchaseData;
  pollingContext: IPollingContext;
}

interface IUseScratchPayPurchaseFlowParams {
  onPollingEnd?: () => void;
  onDenied?: () => void;
}

export const useScratchPayPurchaseFlow = ({ onPollingEnd, onDenied }: IUseScratchPayPurchaseFlowParams = {}) => {
  const {
    initiatePurchase: initiatePurchaseMutation,
    loading: scratchPayPurchaseLoading,
    error: scratchPayPurchaseError,
    reset: resetScratchPayPurchase,
  } = useScratchPayPurchase();

  const { isPolling, startPolling, cancelPolling } = useScratchPayPolling({ onPollingEnd, onDenied });

  const initiatePurchase = useCallback(
    ({ purchaseData, pollingContext }: IInitiatePurchaseParams) => {
      userActivityLogger(MIXPANEL_EVENTS.SCRATCHPAY_PURCHASE_INITIATED, {
        pet_id: pollingContext.petId,
        pet_name: pollingContext.petName,
        plan_name: pollingContext.planName,
        plan_type: "Monthly",
        payment_method: "ScratchPay",
      });

      const scratchPayWindow = window.open("about:blank", "_blank");

      initiatePurchaseMutation({
        variables: { data: purchaseData },
        context: { suppressGlobalErrorToast: true },
        onCompleted: (response) => {
          const { scratchpayUrl, uniqueTransactionId } = response.scratchpayPurchase;

          userActivityLogger(MIXPANEL_EVENTS.SCRATCHPAY_REDIRECT_TO_EXTERNAL, {
            pet_id: pollingContext.petId,
            pet_name: pollingContext.petName,
            plan_name: pollingContext.planName,
            unique_transaction_id: uniqueTransactionId,
            payment_method: "ScratchPay",
          });

          if (scratchPayWindow && !scratchPayWindow.closed) {
            scratchPayWindow.location.href = scratchpayUrl;
          } else {
            window.location.href = scratchpayUrl;
          }

          startPolling(uniqueTransactionId, pollingContext);
        },
        onError: (error) => {
          if (scratchPayWindow && !scratchPayWindow.closed) {
            scratchPayWindow.close();
          }

          userActivityLogger(MIXPANEL_EVENTS.SCRATCHPAY_PAYMENT_ERROR, {
            pet_id: pollingContext.petId,
            pet_name: pollingContext.petName,
            plan_name: pollingContext.planName,
            payment_method: "ScratchPay",
            error_message: error.message,
          });
        },
      });
    },
    [initiatePurchaseMutation, startPolling],
  );

  return {
    initiatePurchase,
    scratchPayPurchaseLoading,
    scratchPayPurchaseError,
    isPolling,
    cancelPolling,
    resetScratchPayPurchase,
  };
};

